import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, LifeBuoy, X } from "lucide-react";
import api from "@/lib/api";
import { customerRulesPath, expertRulesPath, useBusinessRules } from "@/lib/businessRules";

const CATEGORY_OPTIONS = [
  ["payment", "Betaling"],
  ["dispute", "Uenighed om aftalen"],
  ["safety", "Utryg adfærd"],
  ["other", "Andet"],
];

export default function CancellationAndSupport({ problem, role, onUpdated }) {
  const [panel, setPanel] = useState("");
  const [reason, setReason] = useState("");
  const [category, setCategory] = useState("dispute");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const { rules } = useBusinessRules();

  const cancellation = useMemo(() => {
    if (!rules) return { refund: null, hours: null };
    if (!problem?.scheduled_at || role === "expert") return { refund: rules.expert_cancellation_refund_percent, hours: null };
    const hours = Math.max(0, (new Date(problem.scheduled_at).getTime() - Date.now()) / 3_600_000);
    return {
      refund: hours >= rules.cancellation_boundary_hours
        ? rules.customer_early_cancellation_refund_percent
        : rules.customer_late_cancellation_refund_percent,
      hours,
    };
  }, [problem?.scheduled_at, role, rules]);

  if (!problem?.accepted_expert_user_id) return null;

  const active = problem.agreement_status === "active";

  const cancelAgreement = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/problems/${problem.id}/cancel`, { confirmed: true, reason });
      onUpdated?.(data);
      setMessage(data.financial_text || "Aftalen er aflyst.");
      setPanel("");
      setReason("");
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Aftalen kunne ikke aflyses.");
    } finally {
      setBusy(false);
    }
  };

  const createCase = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post(`/problems/${problem.id}/support-cases`, { category, description });
      setMessage("Din sag er sendt til SpørgMig support. Du kan fortsætte dialogen i chatten.");
      setPanel("");
      setDescription("");
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Sagen kunne ikke sendes.");
    } finally {
      setBusy(false);
    }
  };

  return <section className="mt-5 rounded-2xl border border-brand-line bg-white p-4 sm:p-5" data-testid="cancellation-and-support">
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <h2 className="font-display text-lg font-semibold text-brand-ink">Aflysning og hjælp</h2>
        <p className="mt-1 max-w-2xl text-sm leading-6 text-brand-muted">Aflys aftalen eller opret en sag hvis der er uenighed om betaling, aftalen eller adfærd.</p>
      </div>
      <div className="flex flex-wrap gap-2">
        {active && <button type="button" onClick={() => { setPanel("cancel"); setError(""); }} className="chip !border-[#D9A6A6] !text-[#991B1B]" data-testid="open-cancellation">Aflys aftale</button>}
        <button type="button" onClick={() => { setPanel("support"); setError(""); }} className="chip"><LifeBuoy size={14}/> Få hjælp</button>
      </div>
    </div>

    {problem.agreement_status === "cancelled" && <div className="mt-4 rounded-xl bg-[#FFF8E7] p-4 text-sm leading-6 text-[#6B4F00]">
      Aftalen er aflyst. Kunden har krav på {problem.customer_refund_percent ?? 100}% tilbage.
      {problem.cancellation_reason && <span className="mt-2 block">Begrundelse: {problem.cancellation_reason}</span>}
    </div>}
    {message && <div className="mt-4 rounded-xl bg-brand-soft p-4 text-sm leading-6 text-brand-green" role="status">{message}</div>}
    {error && <div className="mt-4 rounded-xl bg-[#FFF1EE] p-4 text-sm text-[#991B1B]" role="alert">{error}</div>}

    {panel === "cancel" && <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/45 p-0 sm:items-center sm:p-4" role="dialog" aria-modal="true" aria-labelledby="cancel-title">
      <form onSubmit={cancelAgreement} className="w-full max-w-lg rounded-t-[24px] bg-white p-6 shadow-2xl sm:rounded-[24px]">
        <div className="flex items-start justify-between gap-4"><div><h2 id="cancel-title" className="text-xl font-bold text-brand-ink">Aflys aftalen</h2><p className="mt-1 text-sm text-brand-muted">Aflysningen kan ikke fortrydes.</p></div><button type="button" onClick={() => setPanel("")} aria-label="Luk"><X size={20}/></button></div>
        <div className="mt-5 rounded-xl border border-[#E8D39A] bg-[#FFF9E8] p-4 text-sm leading-6 text-[#6B4C13]">
          <div className="flex gap-2 font-semibold"><AlertTriangle size={18} className="mt-0.5 shrink-0"/>Hvad sker der med betalingen?</div>
          <p className="mt-2">{!rules
            ? "Henter de aktuelle afbudsregler…"
            : role === "expert"
              ? `Når du aflyser får kunden ${rules.expert_cancellation_refund_percent} % tilbage.`
              : cancellation.refund === rules.customer_early_cancellation_refund_percent
                ? `Du aflyser mindst ${rules.cancellation_boundary_hours} timer før og får ${cancellation.refund} % tilbage.`
                : `Du aflyser under ${rules.cancellation_boundary_hours} timer før og får ${cancellation.refund} % tilbage. De resterende ${100 - cancellation.refund} % reserveres til eksperten.`}</p>
          <Link to={role === "expert" ? expertRulesPath : customerRulesPath} className="mt-2 inline-flex font-semibold underline">Se alle regler</Link>
        </div>
        <label className="mt-5 block text-sm font-semibold text-brand-ink">Hvorfor aflyser du?
          <textarea required minLength={3} maxLength={500} value={reason} onChange={(event) => setReason(event.target.value)} rows={4} className="mt-2 w-full rounded-xl border border-brand-line p-3 font-normal outline-none focus:border-brand-fresh focus:ring-4 focus:ring-brand-fresh/10"/>
        </label>
        <div className="mt-5 flex justify-end gap-2"><button type="button" onClick={() => setPanel("")} className="btn-secondary">Behold aftalen</button><button disabled={busy || !rules} className="rounded-full bg-[#991B1B] px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50">{busy ? "Aflyser…" : "Bekræft aflysning"}</button></div>
      </form>
    </div>}

    {panel === "support" && <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/45 p-0 sm:items-center sm:p-4" role="dialog" aria-modal="true" aria-labelledby="support-title">
      <form onSubmit={createCase} className="w-full max-w-lg rounded-t-[24px] bg-white p-6 shadow-2xl sm:rounded-[24px]">
        <div className="flex items-start justify-between gap-4"><div><h2 id="support-title" className="text-xl font-bold text-brand-ink">Få hjælp fra support</h2><p className="mt-1 text-sm text-brand-muted">Sagen knyttes til denne aftale så support kan se forløbet.</p></div><button type="button" onClick={() => setPanel("")} aria-label="Luk"><X size={20}/></button></div>
        <label className="mt-5 block text-sm font-semibold text-brand-ink">Hvad drejer sagen sig om?
          <select value={category} onChange={(event) => setCategory(event.target.value)} className="mt-2 min-h-12 w-full rounded-xl border border-brand-line bg-white px-3 font-normal">{CATEGORY_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>
        </label>
        <label className="mt-5 block text-sm font-semibold text-brand-ink">Beskriv hvad der er sket
          <textarea required minLength={10} maxLength={2000} value={description} onChange={(event) => setDescription(event.target.value)} rows={5} className="mt-2 w-full rounded-xl border border-brand-line p-3 font-normal outline-none focus:border-brand-fresh focus:ring-4 focus:ring-brand-fresh/10"/>
        </label>
        <p className="mt-2 text-xs leading-5 text-brand-muted">Skriv ikke adgangskoder eller kortoplysninger.</p>
        <div className="mt-5 flex justify-end gap-2"><button type="button" onClick={() => setPanel("")} className="btn-secondary">Annuller</button><button disabled={busy} className="btn-primary">{busy ? "Sender…" : "Send sag"}</button></div>
      </form>
    </div>}
  </section>;
}

